import { ValidationRoutingNumberResponse } from "@ftdr/cscpayments-js-client/lib/types/types";
import {
  common,
  payment3_paymentmethodpb,
} from "@ftdr/payment3_paymentmethod_coordinator-js-client";
import {
  SetupIntentResult,
  StripeCardCvcElement,
  StripeCardExpiryElement,
  StripeCardNumberElement,
} from "@stripe/stripe-js";
import { FieldErrors } from "react-hook-form";
import { FormsSetupType, UpdateDefaultPaymentMethodResponseType } from "../..";
import {
  IIdentity,
  MethodSetupResponse,
  RegistrationRequest,
  RegistrationResponse,
  RequireAtLeastOne,
  SecureResponse,
  UuidType,
} from "../../types";
import {
  StripeBillingDetails,
  StripeFormDetails,
} from "./stripe-card-processor/components";
import { paypalViewComponents } from "./paypal-processor";
import { PaymentMethod } from "../../models";

export type PaymentProcessorProps<
  Config extends Record<string, unknown> = Record<string, unknown>
> = {
  config: Config;
};

export type PaymentProcessorResponse = {
  onSuccess: (response: SuccessResponseType) => void;
  onFailure: (response: FailureResponseType) => void;
  onResponse: (response: ResponseType) => void;
};

export type SuccessType =
  | "stripe_success"
  | "ach_success"
  | "registration_success"
  | "ach_valid_number"
  | "valid_routing_number"
  | "not_valid_routing_number"
  | "paymentus_success"
  | "paypal_success";

export const SuccessTypeValue: Record<string, SuccessType> = {
  STRIPE_SUCCESS: "stripe_success",
  REGISTRATION_SUCCESS: "registration_success",
  ACH_SUCCESS: "ach_success",
  VALID_ROUTING_NUMBER: "valid_routing_number",
  NOT_VALID_ROUTING_NUMBER: "not_valid_routing_number",
  PAYMENTUS_SUCCESS: "paymentus_success",
  PAYPAL_SUCCESS: "paypal_success",
};

export type AchSuccessResponse = {
  bankAccountNumber: string;
  bankRoutingNumber: string;
};

export type SuccessResponseType = {
  type: SuccessType;
  stripeSuccessResponse?: MethodSetupResponse;
  achSuccessResponse?: AchSuccessResponse;
  paymentusSuccessResponse?: MethodSetupResponse;
  paypalSuccessResponse?: MethodSetupResponse;
};

export type FailureType =
  | "stripe_failure"
  | "paypal_failure"
  | "ach_failure"
  | "not_valid_routing_number"
  | "not_valid_ach_form"
  | "not_valid_stripe_form"
  | "redis_cache_404_error"
  | "paymentus_failure"
  | "not_valid_paymentus_form"
  | "token_failure"
  | "no_filled_paypal_information";

export const FailureTypeValue: Record<string, FailureType> = {
  STRIPE_FAILURE: "stripe_failure",
  PAYPAL_FAILURE: "paypal_failure",
  ACH_FAILURE: "ach_failure",
  NOT_VALID_ROUTING_NUMBER: "not_valid_routing_number",
  NOT_VALID_ACH_FORM: "not_valid_ach_form",
  NOT_VALID_PAYMENTUS_FORM: "not_valid_paymentus_form",
  NOT_VALID_STRIPE_FORM: "not_valid_stripe_form",
  REDIS_CACHE_404_ERROR: "redis_cache_404_error",
  PAYMENTUS_FAILURE: "paymentus_failure",
  TOKEN_FAILURE: "token_failure",
  NO_FILLED_PAYPAL_INFO: "no_filled_paypal_information",
};

export type DecodedErrorType = common.IError[];

export type ErrorType = {
  message?: string;
  decodedError?: DecodedErrorType;
  rawError?: Record<string, unknown>;
  code?: string | number;
  uuid?: UuidType;
};

export interface IFormInput {
  bankRoutingNumber: string;
  bankAccountNumber: string;
  bankAccountNumberConfirmation: string;
}

export interface IPaymentusFormInput {
  bankRoutingNumber: string;
  bankAccountNumber: string;
}

export type FailureResponseType = {
  type?: FailureType;
  stripeFailureResponse?: ErrorType;
  paypalFailureResponse?: ErrorType;
  failureValidationResponse?: ValidationRoutingNumberResponse;
  achFormErrors?: FieldErrors<IFormInput>;
  achFailureResponse?: ErrorType;
  stripeFormErrors?: FieldErrors<StripeFormDetails>;
  paymentusFormErrors?: FieldErrors<IPaymentusFormInput>;
  paymentusFailureResponse?: ErrorType;
  tokenError?: ErrorType;
};

export type OnResponseType =
  | "registration_success"
  | "setup_card_success"
  | "setup_card_intent"
  | "tokenization_success"
  | SuccessType
  | FailureType;

export const OnResponseTypeValue: Record<string, OnResponseType> = {
  REGISTRATION_SUCCESS: "registration_success",
  TOKENIZATION_SUCCESS: "tokenization_success",
  SETUP_CARD_SUCCESS: "setup_card_success",
  SETUP_CARD_INTENT: "setup_card_intent",
  ...SuccessTypeValue,
};

export type GeneralStatus =
  | "payment_success"
  | "payment_failed"
  | "payment_success_set_default_success"
  | "payment_success_set_default_failed";

export enum GeneralStatusValue {
  PAYMENT_SUCCESS = "payment_success",
  PAYMENT_FAILED = "payment_failed",
  PAYMENT_SUCCESS_SET_DEFAULT_SUCCESS = "payment_success_set_default_success",
  PAYMENT_SUCCESS_SET_DEFAULT_FAILED = "payment_success_set_default_failed",
}

export type ResponseType = {
  type: OnResponseType;
  registrationSuccessResponse?: RegistrationResponse;
  setupCardSuccessResponse?: Partial<SetupIntentResult>;
  successValidationResponse?: ValidationRoutingNumberResponse;
  tokenizationSuccessResponse?: SecureResponse;
} & Partial<
  Pick<
    SuccessResponseType,
    | "stripeSuccessResponse"
    | "achSuccessResponse"
    | "paymentusSuccessResponse"
    | "paypalSuccessResponse"
  >
>;

export type PaymentProcessorClientInternalRef<
  RequestType,
  Client = Record<string, unknown> | undefined
> = {
  getClient: () => Client;
  processPayment: ProcessPayment<RequestType>;
};

export type ProcessPaymentRequestData =
  | ProcessPaymentStripeRequestData
  | ProcessPaymentACHRequestData
  | ProcessPaymentPaymentusRequestData
  | ProcessPaymentPaypalRequestData;

export type ProcessPaymentStripeRequestData = {
  stripe?: {
    owner?: {
      ownerIdentity: {
        type: payment3_paymentmethodpb.OwnerIdentityType | null;
      } & RequireAtLeastOne<
        IIdentity,
        | "contractIdentity"
        | "customerIdentity"
        | "replacementIdentity"
        | "dispatchIdentity"
        | "orderIdentity"
        | "quoteIdentity"
        | "externalCustomerIdentity"
        | "externalContractIdentity"
      >;
    };
    billingDetails?: StripeBillingDetails;
  } & Pick<
    payment3_paymentmethodpb.IPaymentMethod,
    "owner" | "includedInWallet"
  >;
  postal_code?: string;
};
export type ProcessPaymentACHRequestData = {
  ach?: Record<string, unknown>;
};

export type ProcessPaymentPaypalRequestData = {
  paypal?: {
    owner?: {
      ownerIdentity: {
        type: payment3_paymentmethodpb.OwnerIdentityType | null;
      } & RequireAtLeastOne<
        IIdentity,
        | "contractIdentity"
        | "customerIdentity"
        | "replacementIdentity"
        | "dispatchIdentity"
        | "orderIdentity"
        | "quoteIdentity"
        | "externalCustomerIdentity"
        | "externalContractIdentity"
      >;
    };
  } & Pick<
    payment3_paymentmethodpb.IPaymentMethod,
    "owner" | "includedInWallet"
  > &
    Partial<Pick<payment3_paymentmethodpb.IPaymentMethod, "source">>;
};

export type PaypalProcessorScriptProviderProps = {
  components: Omit<PaypalProcessorComponents, "ErrorBoundaryComponent">;
};

export type ProcessPaymentPaymentusRequestData = {
  paymentus?: {
    owner?: {
      ownerIdentity: {
        type: payment3_paymentmethodpb.OwnerIdentityType | null;
      } & RequireAtLeastOne<
        IIdentity,
        | "contractIdentity"
        | "customerIdentity"
        | "replacementIdentity"
        | "dispatchIdentity"
        | "orderIdentity"
        | "quoteIdentity"
        | "externalCustomerIdentity"
        | "externalContractIdentity"
      >;
    };
  } & Pick<
    payment3_paymentmethodpb.IPaymentMethod,
    "owner" | "includedInWallet"
  > &
    Partial<
      Pick<
        payment3_paymentmethodpb.IPaymentMethod,
        "source" | "brand" | "subbrand"
      >
    >;
};

export type AgregatedProcessPaymentResponse = {
  stripe?: {
    type?: OnResponseType;
  } & Pick<SuccessResponseType, "stripeSuccessResponse"> &
    Pick<FailureResponseType, "stripeFailureResponse" | "stripeFormErrors">;
  ach?: {
    type?: OnResponseType;
  } & Pick<SuccessResponseType, "achSuccessResponse"> &
    Pick<FailureResponseType, "achFormErrors">;
  paymentus?: {
    type?: OnResponseType;
  } & Pick<SuccessResponseType, "paymentusSuccessResponse"> &
    Pick<
      FailureResponseType,
      "paymentusFailureResponse" | "paymentusFormErrors"
    >;
  paypal?: {
    type?: OnResponseType;
  } & Pick<SuccessResponseType, "paypalSuccessResponse"> &
    Pick<FailureResponseType, "paypalFailureResponse">;
  generalStatus: GeneralStatus;
  setAsDefault?: UpdateDefaultPaymentMethodResponseType<PaymentMethod>;
};

export type ProcessPayment<T> = (
  /**
   * @deprecated only use this for postal_code and includedInWallet. use props for applicationName and owner;
   * this helps us send these in registration processes and
   * track issues more easily.
   */
  requestData?: T,
  resolve?: (result: AgregatedProcessPaymentResponse) => void
) => Promise<AgregatedProcessPaymentResponse | undefined> | undefined;

export type MessageActionType =
  | "submit"
  | "iframe_loaded"
  | "validation_form"
  | "payment_secure_service"
  | "create_payment_method"
  | "trigger_registration"
  | "paymentus_form_config";

export type MessageStatusType =
  | "paymentus_registration_success"
  | "paymentus_registration_error"
  | "create_payment_method_success"
  | "create_payment_method_error"
  | "create_payment_method_redis_cache_error"
  | "payment_secure_service_success"
  | "payment_secure_service_error"
  | "paymentus_form_validation"
  | "paymentus_form_status"
  | "paymentus_iframe_on_ready"
  | "paymentus_iframe_height";

export const MESSAGE_ACTION: Record<string, MessageActionType> = {
  SUBMIT: "submit",
  IFRAME_LOADED: "iframe_loaded",
  VALIDATION_FORM: "validation_form",
  CREATE_PAYMENT_METHOD: "create_payment_method",
  TRIGGER_REGISTRATION: "trigger_registration",
  PAYMENTUS_FORM_CONFIG: "paymentus_form_config",
};

export const MESSAGE_STATUS: Record<string, MessageStatusType> = {
  PAYMENTUS_REGISTRATION_SUCCESS: "paymentus_registration_success",
  PAYMENTUS_REGISTRATION_ERROR: "paymentus_registration_error",
  CREATE_PAYMENT_METHOD_SUCCESS: "create_payment_method_success",
  CREATE_PAYMENT_METHOD_ERROR: "create_payment_method_error",
  CREATE_PAYMENT_METHOD_REDIS_CACHE_ERROR:
    "create_payment_method_redis_cache_error",
  PAYMENT_SECURE_SERVICE_SUCCESS: "payment_secure_service_success",
  PAYMENT_SECURE_SERVICE_ERROR: "payment_secure_service_error",
  PAYMENTUS_FORM_INVALID_FIELDS: "paymentus_form_validation",
  PAYMENTUS_FORM_STATUS: "paymentus_form_status",
  PAYMENTUS_IFRAME_ON_READY: "paymentus_iframe_on_ready",
  PAYMENTUS_IFRAME_HEIGHT: "paymentus_iframe_height",
};

export type MessageType = {
  action?: MessageActionType;
  status?: MessageStatusType;
  error?: {
    registration_error?: ErrorType;
    paymentus_form_error?: FieldErrors<IPaymentusFormInput>;
    payment_secure_service_error?: ErrorType;
    create_payment_method_error?: ErrorType;
  };
  data?: {
    registration_request_data?: RegistrationRequest;
    registration_data?: RegistrationResponse;
    payment_secure_service_data?: SecureResponse;
    create_payment_method_data?: MethodSetupResponse;
    submit_data?: ProcessPaymentPaymentusRequestData;
    paymentus_form_config?: FormsSetupType["paymentusForm"];
    paymentus_form_status?: FormStatus;
    iframe_data?: IframeData;
    test_id_name?: string;
  };
};

export type IframeData = {
  iframe_identifier?: string;
  iframe_height?: number;
};

export type FormStatus = {
  isCompleted?: boolean;
};

export type PaypalProcessorComponents = typeof paypalViewComponents;

export type StripeElement =
  | ((
      | StripeCardNumberElement
      | StripeCardExpiryElement
      | StripeCardCvcElement
    ) & {
      _empty?: string;
      _invalid?: boolean;
      _parent?: HTMLElement;
    })
  | null
  | undefined;
